import Breadcrumb from '../components/Breadcrumb';
import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useRef } from 'react';
import { WRAP, SECTION_PAD } from '../styles/layout';
import { Helmet } from 'react-helmet-async';

/* ══════════════════════════════════════════════════════════════════════════
   REVMIGRATE — AI DBA Product Page  (v2 — mobile/tablet responsive)
   Brand: Amber #F59E0B  rgb: 245,158,11
   ══════════════════════════════════════════════════════════════════════════ */

const C  = '#F59E0B';
const R  = '245,158,11';
const BG = '#09060F';

/* ── Breakpoint hook ─────────────────────────────────────────────────────
   mob = < 768px  (phone)
   tab = < 1024px (tablet / narrow desktop)
   ─────────────────────────────────────────────────────────────────────── */
function useBreakpoint() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return { mob: w < 768, tab: w < 1024 };
}

/* ── Reveal / Counter helpers ────────────────────────────────────────── */
function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function Counter({ to, prefix = '', suffix = '', duration = 1800 }) {
  const [val, setVal] = useState(0);
  const [ref, visible] = useReveal(0.3);
  const started = useRef(false);
  useEffect(() => {
    if (!visible || started.current) return;
    started.current = true;
    const start = performance.now();
    const tick = now => {
      const p = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(ease * to));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [visible, to, duration]);
  return <span ref={ref}>{prefix}{val.toLocaleString()}{suffix}</span>;
}

function Reveal({ children, delay = 0, style = {} }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'none' : 'translateY(26px)',
      transition: `opacity .7s ease ${delay}ms, transform .7s ease ${delay}ms`,
      ...style,
    }}>{children}</div>
  );
}

const Section = ({ children, style = {} }) => (
  <section style={{ padding: `${SECTION_PAD} 0`, position: 'relative', ...style }}>
    {children}
  </section>
);
const Cap = ({ children, style = {} }) => (
  <div style={{ ...WRAP, ...style }}>{children}</div>
);
const Eyebrow = ({ children }) => (
  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:3, textTransform:'uppercase', color:C, margin:'0 0 16px', opacity:.85 }}>{children}</p>
);
const H2 = ({ children, style = {} }) => (
  <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, lineHeight:1.1, fontSize:'clamp(28px,2.8vw,46px)', color:'#EDE9F8', margin:'0 0 20px', letterSpacing:'-1px', ...style }}>{children}</h2>
);
const Body = ({ children, style = {} }) => (
  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:'clamp(14px,1.1vw,17px)', fontWeight:300, lineHeight:1.8, color:'#9B90BA', margin:'0 0 16px', ...style }}>{children}</p>
);

/* ══════════════════════════════════════════════════════════════════════════
   AGENT INBOX — hero right panel
   ══════════════════════════════════════════════════════════════════════════ */
function AgentInbox() {
  const [activeItem, setActiveItem] = useState(0);
  const [approvedItems, setApprovedItems] = useState([]);

  const items = [
    {
      icon:'📋', stage:'01 — Plan', label:'Migration Plan Approval',
      urgency:'Review required', color:'#3B82F6', rgb:'59,130,246',
      summary:'AI scanned 2,847 database objects across Oracle 19c. Migration plan generated.',
      detail:'342 tables · 89 stored procedures · 24 views · 6 functions · 4 triggers',
      aiRec:'Recommended: Phased migration over 3 weeks. 847 objects auto-convertible. 12 require manual review.',
      risk:'Low — 94% of objects have direct PostgreSQL equivalents.',
      effort:'Est. 18 days → RevMigrate estimate: 4 days',
    },
    {
      icon:'💰', stage:'02 — Convert', label:'Infrastructure Cost Approval',
      urgency:'Budget sign-off', color:'#F59E0B', rgb:'245,158,11',
      summary:'Target cloud infrastructure costed before any provisioning.',
      detail:'RDS PostgreSQL db.r6g.2xlarge · 2TB gp3 storage · Multi-AZ standby',
      aiRec:'Estimated monthly cost: $1,847/mo. One-time migration compute: ~$340. Recommended: Reserved instance for 31% saving.',
      risk:'No infrastructure provisioned until you approve.',
      effort:'$1,847/mo ongoing · $340 migration compute',
    },
    {
      icon:'🔒', stage:'02 — Convert', label:'Schema Change Approval',
      urgency:'Irreversible action', color:'#EF4444', rgb:'239,68,68',
      summary:'Converted schema ready to deploy to target database.',
      detail:'342 tables converted · 3 naming conflicts resolved · 2 data type promotions',
      aiRec:'All changes reviewed. PATIENT_ID promoted from NUMBER(10) to BIGINT. Created_at timezone-aware. Recommend review of 2 nullable constraint changes.',
      risk:'Irreversible — schema deployment cannot be undone. Staging environment recommended.',
      effort:'Schema deploy: ~4 min. Rollback: not available after apply.',
    },
    {
      icon:'🚚', stage:'03 — Deploy', label:'Data Migration Approval',
      urgency:'Production data move', color:'#EF4444', rgb:'239,68,68',
      summary:'All validation passed. Ready to move production data.',
      detail:'2.3TB production data · 847M rows · estimated 6h 14m transfer window',
      aiRec:'Pre-migration validation: 100% passed. Recommended: begin at 02:00 UTC Sunday. Parallel reads enabled. Rollback checkpoint created.',
      risk:'High — production data movement. Downtime window required.',
      effort:'6h 14m transfer · 45min validation · 15min cutover',
    },
    {
      icon:'✅', stage:'04 — Validate', label:'Final Validation Sign-Off',
      urgency:'Go-live approval', color:'#10B981', rgb:'16,185,129',
      summary:'Automated validation complete. All checks passed.',
      detail:'Row counts: 100% match · Data integrity: 100% · Query performance: +23% faster',
      aiRec:'All 847M rows validated. Zero data loss detected. 94% of queries performing equal or better on PostgreSQL. 3 queries flagged for index optimisation.',
      risk:'Low — all automated checks passed. Ready for production cutover.',
      effort:'Migration complete · PDF report generated · Cutover ready',
    },
  ];

  const item = items[activeItem];
  const isApproved = approvedItems.includes(activeItem);

  const approve = () => {
    setApprovedItems(prev => [...prev, activeItem]);
    setTimeout(() => {
      if (activeItem < items.length - 1) setActiveItem(i => i + 1);
    }, 800);
  };

  return (
    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", animation:'fadeUp .9s ease both .55s' }}>
      {/* Title bar */}
      <div style={{ padding:'11px 16px', background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)', borderRadius:'12px 12px 0 0', borderBottom:'none', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ display:'flex', gap:5 }}>
            {['#EF4444','#F59E0B','#10B981'].map((c,i) => <div key={i} style={{ width:9, height:9, borderRadius:'50%', background:c, opacity:.65 }}/>)}
          </div>
          <span style={{ fontSize:11, fontWeight:600, color:'rgba(200,192,236,0.55)', letterSpacing:.5 }}>RevMigrate — Agent Inbox</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <div style={{ width:5, height:5, borderRadius:'50%', background:C, animation:'pulse 2s infinite' }}/>
          <span style={{ fontSize:10, color:`rgba(${R},.75)`, fontWeight:700, letterSpacing:1, textTransform:'uppercase' }}>Live</span>
        </div>
      </div>

      {/* Stage progress */}
      <div style={{ background:'rgba(255,255,255,.025)', border:'1px solid rgba(255,255,255,.07)', borderTop:'none', padding:'14px 16px 10px' }}>
        <div style={{ fontSize:10, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:'rgba(200,192,236,0.35)', marginBottom:10 }}>Migration Pipeline</div>
        <div style={{ display:'flex', gap:4 }}>
          {['Plan','Convert','Deploy','Validate'].map((s, i) => {
            const stageApproved = (i===0&&approvedItems.includes(0))||(i===1&&approvedItems.includes(1)&&approvedItems.includes(2))||(i===2&&approvedItems.includes(3))||(i===3&&approvedItems.includes(4));
            const stageActive   = (i===0&&activeItem===0)||(i===1&&(activeItem===1||activeItem===2))||(i===2&&activeItem===3)||(i===3&&activeItem===4);
            return (
              <div key={i} style={{ flex:1, textAlign:'center', padding:'6px 4px', borderRadius:6, background:stageApproved?`rgba(${R},.12)`:stageActive?`rgba(${R},.07)`:'rgba(255,255,255,.025)', border:`1px solid ${stageApproved||stageActive?`rgba(${R},.25)`:'rgba(255,255,255,.07)'}`, transition:'all .3s ease' }}>
                <div style={{ fontSize:9, fontWeight:700, color:stageApproved?C:stageActive?`rgba(${R},.8)`:'rgba(200,192,236,0.45)', letterSpacing:.5 }}>0{i+1}</div>
                <div style={{ fontSize:10, fontWeight:stageActive?700:400, color:stageApproved||stageActive?'#DDD8F2':'rgba(200,192,236,0.45)', marginTop:2 }}>{s}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Active approval card */}
      <div style={{ background:'rgba(255,255,255,.02)', border:'1px solid rgba(255,255,255,.07)', borderTop:'none' }}>
        <div style={{ padding:'14px 16px', borderBottom:'1px solid rgba(255,255,255,.05)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:16 }}>{item.icon}</span>
            <div>
              <div style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:`rgba(${item.rgb},.75)` }}>{item.stage}</div>
              <div style={{ fontSize:12, fontWeight:700, color:'#C8C0EC', marginTop:1 }}>{item.label}</div>
            </div>
          </div>
          <div style={{ padding:'3px 10px', borderRadius:100, background:isApproved?`rgba(16,185,129,.15)`:`rgba(${item.rgb},.12)`, border:`1px solid ${isApproved?'rgba(16,185,129,.35)':`rgba(${item.rgb},.3)`}`, fontSize:9, fontWeight:700, letterSpacing:1, textTransform:'uppercase', color:isApproved?'#10B981':item.color, transition:'all .3s ease' }}>
            {isApproved ? '✓ Approved' : item.urgency}
          </div>
        </div>

        <div style={{ padding:'14px 16px' }}>
          <p style={{ fontSize:12, fontWeight:300, color:'#C8C0EC', margin:'0 0 10px', lineHeight:1.6 }}>{item.summary}</p>
          <div style={{ padding:'8px 12px', background:'rgba(255,255,255,.03)', borderRadius:6, marginBottom:10 }}>
            <span style={{ fontSize:11, color:'#9B90BA' }}>{item.detail}</span>
          </div>
          <div style={{ padding:'10px 12px', background:`rgba(${R},.06)`, border:`1px solid rgba(${R},.15)`, borderRadius:7, marginBottom:10 }}>
            <div style={{ fontSize:9, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:`rgba(${R},.6)`, marginBottom:5 }}>AI Recommendation</div>
            <p style={{ fontSize:11.5, fontWeight:300, lineHeight:1.6, color:'#C8C0EC', margin:0 }}>{item.aiRec}</p>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, marginBottom:12 }}>
            <div style={{ padding:'7px 10px', background:'rgba(255,255,255,.025)', borderRadius:6 }}>
              <div style={{ fontSize:9, fontWeight:700, letterSpacing:1, color:'rgba(200,192,236,0.38)', marginBottom:3, textTransform:'uppercase' }}>Risk</div>
              <div style={{ fontSize:11, color:'#C8C0EC' }}>{item.risk}</div>
            </div>
            <div style={{ padding:'7px 10px', background:'rgba(255,255,255,.025)', borderRadius:6 }}>
              <div style={{ fontSize:9, fontWeight:700, letterSpacing:1, color:'rgba(200,192,236,0.38)', marginBottom:3, textTransform:'uppercase' }}>Effort</div>
              <div style={{ fontSize:11, color:'#C8C0EC' }}>{item.effort}</div>
            </div>
          </div>
          {!isApproved ? (
            <div style={{ display:'flex', gap:6 }}>
              <button onClick={approve} style={{ flex:1, padding:'9px 0', borderRadius:7, cursor:'pointer', background:C, color:'#09060F', border:'none', fontSize:12, fontWeight:800, fontFamily:"'DM Sans',system-ui,sans-serif", boxShadow:`0 4px 14px rgba(${R},.4)`, transition:'all .15s ease' }}
                onMouseEnter={e=>e.currentTarget.style.transform='translateY(-1px)'}
                onMouseLeave={e=>e.currentTarget.style.transform='none'}
              >Approve →</button>
              <button style={{ padding:'9px 14px', borderRadius:7, cursor:'pointer', background:'transparent', color:'#9B90BA', border:'1px solid rgba(255,255,255,.1)', fontSize:12, fontFamily:"'DM Sans',system-ui,sans-serif" }}>Request Changes</button>
            </div>
          ) : (
            <div style={{ padding:'10px', background:'rgba(16,185,129,.08)', border:'1px solid rgba(16,185,129,.2)', borderRadius:7, textAlign:'center' }}>
              <span style={{ fontSize:12, fontWeight:700, color:'#10B981' }}>✓ Approved — AI executing next step</span>
            </div>
          )}
        </div>
      </div>

      {/* Queue footer */}
      <div style={{ padding:'10px 16px', background:'rgba(255,255,255,.02)', border:'1px solid rgba(255,255,255,.06)', borderTop:'1px solid rgba(255,255,255,.04)', borderRadius:'0 0 12px 12px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ display:'flex', gap:6 }}>
          {items.map((_, i) => (
            <div key={i} onClick={()=>setActiveItem(i)} style={{ width:22, height:22, borderRadius:'50%', cursor:'pointer', background:approvedItems.includes(i)?`rgba(${R},.2)`:i===activeItem?`rgba(${R},.12)`:'rgba(255,255,255,.05)', border:`1px solid ${approvedItems.includes(i)?`rgba(${R},.5)`:i===activeItem?`rgba(${R},.3)`:'rgba(255,255,255,.1)'}`, display:'flex', alignItems:'center', justifyContent:'center', transition:'all .2s ease' }}>
              <span style={{ fontSize:9 }}>{approvedItems.includes(i)?'✓':i+1}</span>
            </div>
          ))}
        </div>
        <span style={{ fontSize:10, color:'rgba(200,192,236,0.38)' }}>{approvedItems.length}/{items.length} approved</span>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   HERO
   ══════════════════════════════════════════════════════════════════════════ */
function Hero() {
  const navigate = useNavigate();
  const { mob } = useBreakpoint();

  return (
    <section style={{ background:BG, display:'flex', alignItems:'center', padding:`${SECTION_PAD} 0 clamp(40px,5vh,60px)`, position:'relative', overflow:'hidden' }}>
      <div aria-hidden="true" style={{ position:'absolute', inset:0, pointerEvents:'none' }}>
        <div style={{ position:'absolute', top:'-10%', right:'-8%', width:750, height:750, borderRadius:'50%', background:`radial-gradient(circle,rgba(${R},.09) 0%,transparent 65%)`, filter:'blur(100px)' }}/>
        <div style={{ position:'absolute', bottom:'-5%', left:'-8%', width:600, height:600, borderRadius:'50%', background:'radial-gradient(circle,rgba(45,24,84,.55) 0%,transparent 65%)', filter:'blur(80px)' }}/>
        <div style={{ position:'absolute', inset:0, backgroundImage:'linear-gradient(rgba(255,255,255,.015) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.015) 1px,transparent 1px)', backgroundSize:'80px 80px', maskImage:'radial-gradient(ellipse 80% 60% at 65% 40%,black 0%,transparent 100%)' }}/>
      </div>

      <div style={{ ...WRAP, position:'relative', zIndex:2, width:'100%',
        display:'grid',
        gridTemplateColumns: mob ? '1fr' : '1fr 1fr',
        gap: mob ? '40px' : 'clamp(40px,5vw,80px)',
        alignItems:'center',
      }}>
        {/* LEFT */}
        <div>
          <Breadcrumb
            color={C}
            items={[
              { label: 'Home', path: '/' },
              { label: 'AI Roles', path: '/' },
              { label: 'RevMigrate — AI DBA' },
            ]}
          />
          <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'6px 14px 6px 10px', borderRadius:100, marginBottom:28, background:`rgba(${R},.08)`, border:`1px solid rgba(${R},.22)`, animation:'fadeUp .7s ease both .1s' }}>
            <div style={{ width:6, height:6, borderRadius:'50%', background:C, animation:'pulse 2s infinite' }}/>
            <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12, fontWeight:600, color:`rgba(${R},.85)`, letterSpacing:.3 }}>RevMigrate · AI Database Architect</span>
          </div>

          <h1 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, lineHeight:1.05, letterSpacing:'-2px', fontSize: mob ? '36px' : 'clamp(36px,4vw,66px)', color:'#EDE9F8', margin:'0 0 12px', animation:'fadeUp .8s ease both .2s' }}>
            Every month on a legacy database
          </h1>
          <h1 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, lineHeight:1.05, letterSpacing:'-2px', fontSize: mob ? '36px' : 'clamp(36px,4vw,66px)', color:C, margin:'0 0 28px', animation:'fadeUp .8s ease both .28s' }}>
            is a month you can't ship AI.
          </h1>

          <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:'clamp(15px,1.15vw,18px)', fontWeight:300, lineHeight:1.8, color:'#9B90BA', maxWidth:500, margin:'0 0 40px', animation:'fadeUp .7s ease both .38s' }}>
            RevMigrate is the AI Database Architect that plans, converts, deploys, and validates your migration from expensive legacy databases to modern open-source cloud infrastructure — automatically, with your team in control at every decision that carries real business risk.
          </p>

          <div style={{ display:'flex', gap:12, flexWrap:'wrap', animation:'fadeUp .7s ease both .5s' }}>
            <button style={{ padding:'14px 30px', borderRadius:10, cursor:'pointer', background:C, color:'#09060F', border:'none', fontSize:15, fontWeight:800, fontFamily:"'DM Sans',system-ui,sans-serif", boxShadow:`0 6px 28px rgba(${R},.4)`, transition:'all .22s ease', flex: mob ? '1 1 auto' : 'none' }}
              onClick={()=>{ window.scrollTo(0,0); navigate('/contact'); }}
              onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow=`0 12px 36px rgba(${R},.55)`;}}
              onMouseLeave={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow=`0 6px 28px rgba(${R},.4)`;}}
            >Book a Demo</button>
            <button style={{ padding:'14px 24px', borderRadius:10, cursor:'pointer', background:'transparent', color:'#C8C0EC', border:'1px solid rgba(255,255,255,.12)', fontSize:15, fontWeight:400, fontFamily:"'DM Sans',system-ui,sans-serif", transition:'all .22s ease', flex: mob ? '1 1 auto' : 'none' }}
              onClick={()=>document.getElementById('how-it-works')?.scrollIntoView({behavior:'smooth'})}
              onMouseEnter={e=>{e.currentTarget.style.borderColor='rgba(255,255,255,.28)';e.currentTarget.style.color='rgba(255,255,255,.82)';}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor='rgba(255,255,255,.12)';e.currentTarget.style.color='#C8C0EC';}}
            >Request Migration Assessment ↓</button>
          </div>

          <div style={{ display:'flex', gap:20, marginTop:40, flexWrap:'wrap', animation:'fadeUp .7s ease both .62s' }}>
            {['90% less manual effort','4× faster than traditional','100% audit trail','Oracle · SQL Server → PostgreSQL'].map((t,i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:6 }}>
                <div style={{ width:4, height:4, borderRadius:'50%', background:C, opacity:.65 }}/>
                <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11.5, color:'rgba(200,192,236,0.55)' }}>{t}</span>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT — Agent Inbox, hidden on mobile */}
        {!mob && (
          <div style={{ position:'relative' }}>
            <div aria-hidden="true" style={{ position:'absolute', inset:'-15%', background:`radial-gradient(ellipse 70% 60% at 50% 50%,rgba(${R},.1) 0%,transparent 70%)`, filter:'blur(40px)', pointerEvents:'none' }}/>
            <AgentInbox/>
          </div>
        )}
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   PROVOCATION
   ══════════════════════════════════════════════════════════════════════════ */
function Provocation() {
  const { mob } = useBreakpoint();
  const [ref, visible] = useReveal(0.3);
  return (
    <div id="how-it-works" ref={ref} style={{ background:`rgba(${R},.035)`, borderTop:`1px solid rgba(${R},.1)`, borderBottom:`1px solid rgba(${R},.1)`, padding:'clamp(36px,5vh,52px) clamp(24px,4vw,72px)' }}>
      <div style={{ ...WRAP,
        display:'grid',
        gridTemplateColumns: mob ? '1fr' : '1fr 1fr',
        gap: mob ? '32px' : '64px',
        alignItems:'center',
      }}>
        <p style={{ fontFamily:"'Fraunces',Georgia,serif", fontStyle:'italic', fontWeight:300, fontSize:'clamp(18px,2vw,28px)', lineHeight:1.55, color:'#DDD8F2', margin:0, opacity:visible?1:0, transform:visible?'none':'translateY(16px)', transition:'all .9s ease' }}>
          "The average enterprise database migration takes 18–24 months, consumes 40% of an engineering team's capacity, and still carries a <span style={{ color:C }}>60% risk of going over budget."</span>
        </p>
        <div style={{ opacity:visible?1:0, transform:visible?'none':'translateY(16px)', transition:'all .9s ease .2s' }}>
          <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:'rgba(200,192,236,0.38)', margin:'0 0 16px' }}>What that costs you</p>
          {[
            { bold:'18 months', rest:'of your engineering team not building product, not integrating AI, not shipping features.' },
            { bold:'40% capacity', rest:'burned on SQL conversion that an AI can do overnight — while your team sleeps.' },
            { bold:'60% chance', rest:'you go over budget before a single row moves to production.' },
          ].map((item,i) => (
            <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start', marginBottom:12 }}>
              <div style={{ width:5, height:5, borderRadius:'50%', background:C, marginTop:7, flexShrink:0 }}/>
              <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:14, fontWeight:300, lineHeight:1.7, color:'#9B90BA', margin:0 }}>
                <strong style={{ color:'#DDD8F2', fontWeight:700 }}>{item.bold} </strong>{item.rest}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   PROBLEM — old way vs new way
   ══════════════════════════════════════════════════════════════════════════ */
function Problem() {
  const { mob } = useBreakpoint();
  return (
    <Section style={{ background:BG, borderTop:'1px solid rgba(255,255,255,.05)' }}>
      <Cap>
        <Reveal>
          <Eyebrow>The Problem</Eyebrow>
          <H2 style={{ maxWidth:700 }}>Your legacy database isn't a technology problem.<br/>It's an <em style={{ color:C, fontStyle:'normal' }}>AI-readiness</em> problem.</H2>
          <Body style={{ maxWidth:620 }}>
            Organisations don't stay on Oracle and SQL Server because they want to. They stay because migration has always been a year-long manual project that drains engineering teams and routinely fails. General-purpose AI tools don't solve this — they generate SQL snippets that still need a DBA to review, validate, and stitch together. RevMigrate owns the entire pipeline.
          </Body>
        </Reveal>

        <div style={{ display:'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap:2, marginTop:52 }}>
          <Reveal delay={0}>
            <div style={{ padding:'36px 32px', height:'100%', background:'rgba(239,68,68,.04)', border:'1px solid rgba(239,68,68,.12)' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20 }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:'#EF4444' }}/>
                <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:'rgba(239,68,68,.7)' }}>The Traditional Way</span>
              </div>
              {[
                'Week 1–8: Consultants document the schema. Nothing moves yet.',
                'Week 9–20: Manual stored procedure conversion. One bad SP blocks the entire batch.',
                'Month 6: First estimate of true complexity. Budget explodes.',
                'Month 12: Infrastructure provisioned. First data movement attempted.',
                'Month 18: Go-live. Half the team is exhausted. Compliance audit still outstanding.',
              ].map((item,i) => (
                <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start', marginBottom:10 }}>
                  <span style={{ color:'#EF4444', flexShrink:0, fontSize:12, marginTop:3 }}>✕</span>
                  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:300, lineHeight:1.65, color:'#9B90BA', margin:0 }}>{item}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={mob ? 0 : 100}>
            <div style={{ padding:'36px 32px', height:'100%', background:`rgba(${R},.04)`, border:`1px solid rgba(${R},.18)` }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20 }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:C, boxShadow:`0 0 10px ${C}` }}/>
                <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:`rgba(${R},.7)` }}>The RevMigrate Way</span>
              </div>
              {[
                'Hour 1: AI scans entire database. Full migration plan with effort estimates and risk flags generated.',
                'Hour 2: Your team reviews and approves the plan. One click. Full context provided.',
                'Day 1–3: AI converts every table, procedure, view, and function. Failures isolated — one bad object never blocks the rest.',
                'Day 4: Schema deployed to target with your approval. Infrastructure costed before a single dollar is spent.',
                'Day 5: Automated validation confirms zero data loss. Your team signs off. Migration complete.',
              ].map((item,i) => (
                <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start', marginBottom:10 }}>
                  <span style={{ color:C, flexShrink:0, fontSize:12, marginTop:3 }}>✓</span>
                  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:300, lineHeight:1.65, color:'#9B90BA', margin:0 }}>{item}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </Cap>
    </Section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   PIPELINE — 4 interactive stages
   ══════════════════════════════════════════════════════════════════════════ */
function Pipeline() {
  const { mob, tab } = useBreakpoint();
  const [active, setActive] = useState(0);

  const stages = [
    {
      n:'01', label:'Plan', icon:'◎',
      headline:'Full migration plan in hours, not weeks',
      sub:'AI scans your entire database and produces a complete migration plan — scope, effort estimates, risk flags, object inventory, and recommended approach. Your team reviews and approves before a single object is touched.',
      bullets:[
        'Every table, stored procedure, view, function, and trigger catalogued automatically',
        'Effort estimate per object type — so you know the real project scope before committing',
        'Risk flags surfaced: data sensitivity, compliance exposure, complex dependencies',
        'Recommended migration strategy: phased, big-bang, or parallel run with trade-off analysis',
      ],
      approval:'Migration plan approval — nothing moves forward without your sign-off.',
      time:'Hours, not weeks',
    },
    {
      n:'02', label:'Convert', icon:'⚡',
      headline:'Every object converted. One bad procedure never blocks the rest.',
      sub:"Every table, stored procedure, view, and function is converted to your target database — one object at a time. Failures are isolated. One complex stored procedure doesn't stop 800 others from converting cleanly.",
      bullets:[
        'Oracle → PostgreSQL, SQL Server → PostgreSQL — all major dialect pairs supported',
        'Stored procedures classified: keep in database vs. move to application layer',
        'Natural language override: "Rewrite SP_GetPatient using CTEs instead of temp tables" — AI re-executes',
        'Infrastructure costs surfaced before any provisioning. No surprise bills.',
      ],
      approval:'Schema change approval — irreversible changes are never made automatically.',
      time:'Days, not months',
    },
    {
      n:'03', label:'Deploy', icon:'🚀',
      headline:'Your approval. Then and only then does data move.',
      sub:'Converted schema is deployed to your target database with explicit approval gates before anything irreversible happens. Moving production data is the highest-risk step — RevMigrate stops and waits for your team to confirm before a single row is transferred.',
      bullets:[
        'Schema deployed to staging first — full team review before production',
        'Infrastructure provisioned only after cost approval',
        'Production data movement gated on explicit team confirmation',
        'Parallel run option: old and new database run simultaneously until cutover confidence is high',
      ],
      approval:'Data migration approval — production data never moves without your explicit sign-off.',
      time:'One approved step at a time',
    },
    {
      n:'04', label:'Validate', icon:'✓',
      headline:'Automated validation. Human sign-off. Zero data loss.',
      sub:'After migration, automated checks confirm nothing was lost. Row counts, data integrity validation, and performance benchmarks run automatically. Results are presented to your team for final review before the migration is marked complete.',
      bullets:[
        'Row count validation: every table checked against source — 100% match required',
        'Data integrity checks: FK constraints, nullable rules, data type boundaries',
        'Performance benchmarks: key queries tested against target — regressions flagged',
        'Full PDF migration report generated: what was converted, skipped, validation results',
      ],
      approval:'Final sign-off — migration is never marked complete without your confirmation.',
      time:'Complete in hours',
    },
  ];

  const s = stages[active];

  return (
    <Section style={{ background:`rgba(${R},.025)`, borderTop:`1px solid rgba(${R},.07)`, borderBottom:`1px solid rgba(${R},.07)` }}>
      <Cap>
        <Reveal>
          <Eyebrow>How It Works</Eyebrow>
          <H2>Plan. Convert. Deploy. Validate.</H2>
          <Body style={{ maxWidth:560 }}>Four stages. Zero surprises. Your team approves every decision that carries real business risk. The AI handles everything else.</Body>
        </Reveal>

        {/* Stage selector — scrollable on mobile */}
        <div style={{ display:'flex', gap:0, marginTop:52, borderRadius:12, overflow: mob ? 'visible' : 'hidden', border:'1px solid rgba(255,255,255,.07)', flexWrap: mob ? 'wrap' : 'nowrap' }}>
          {stages.map((st, i) => (
            <button key={i} onClick={()=>setActive(i)} style={{
              flex: mob ? '1 1 calc(50% - 1px)' : '1',
              padding:'18px 8px', cursor:'pointer', border:'none',
              background: i===active?`rgba(${R},.1)`:'rgba(255,255,255,.02)',
              borderRight: mob ? (i%2===0?'1px solid rgba(255,255,255,.06)':'none') : (i<3?'1px solid rgba(255,255,255,.06)':'none'),
              borderBottom: mob ? (i<2?'1px solid rgba(255,255,255,.06)':'none') : 'none',
              transition:'all .2s ease',
            }}>
              <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:11, fontWeight:700, color:i===active?C:'rgba(200,192,236,0.38)', marginBottom:6, letterSpacing:.5 }}>{st.n}</div>
              <div style={{ fontSize:18, marginBottom:5, opacity:i===active?1:.4 }}>{st.icon}</div>
              <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12, fontWeight:i===active?700:400, color:i===active?'#DDD8F2':'rgba(200,192,236,0.55)' }}>{st.label}</div>
              {i===active && <div style={{ width:20, height:2, background:C, margin:'8px auto 0', borderRadius:2 }}/>}
            </button>
          ))}
        </div>

        {/* Stage detail */}
        <div key={active} style={{ display:'grid', gridTemplateColumns: tab ? '1fr' : '1fr 1fr', gap: tab ? '32px' : '60px', marginTop:40, alignItems:'start', animation:'fadeIn .4s ease both' }}>
          <div>
            <div style={{ display:'inline-flex', gap:8, alignItems:'center', marginBottom:16, padding:'4px 12px', borderRadius:100, background:`rgba(${R},.08)`, border:`1px solid rgba(${R},.2)` }}>
              <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, color:C, letterSpacing:1, textTransform:'uppercase' }}>Stage {s.n} — {s.label}</span>
              <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10, color:`rgba(${R},.55)` }}>{s.time}</span>
            </div>
            <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(20px,1.8vw,28px)', fontWeight:700, color:'#EDE9F8', margin:'0 0 16px', lineHeight:1.25 }}>{s.headline}</h3>
            <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:15, fontWeight:300, lineHeight:1.8, color:'#9B90BA', margin:'0 0 28px' }}>{s.sub}</p>
            <div style={{ padding:'16px 20px', background:`rgba(${R},.07)`, border:`1px solid rgba(${R},.2)`, borderRadius:10, display:'flex', gap:12, alignItems:'flex-start' }}>
              <span style={{ fontSize:18, flexShrink:0 }}>🔐</span>
              <div>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:`rgba(${R},.7)`, margin:'0 0 5px' }}>Approval Gate</p>
                <p style={{ fontFamily:"'Fraunces',Georgia,serif", fontStyle:'italic', fontSize:14, fontWeight:300, lineHeight:1.6, color:'#C8C0EC', margin:0 }}>{s.approval}</p>
              </div>
            </div>
          </div>

          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {s.bullets.map((b,i) => (
              <div key={i} style={{ display:'flex', gap:12, alignItems:'flex-start', padding:'15px 18px', background:'rgba(255,255,255,.025)', border:'1px solid rgba(255,255,255,.07)', borderRadius:10, animation:`fadeUp .4s ease ${i*70}ms both`, transition:'all .2s ease', cursor:'default' }}
                onMouseEnter={e=>{e.currentTarget.style.background=`rgba(${R},.06)`;e.currentTarget.style.borderColor=`rgba(${R},.2)`;}}
                onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,.025)';e.currentTarget.style.borderColor='rgba(255,255,255,.07)';}}
              >
                <div style={{ width:5, height:5, borderRadius:'50%', background:C, marginTop:5, flexShrink:0 }}/>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, fontWeight:300, lineHeight:1.7, color:'#C8C0EC', margin:0 }}>{b}</p>
              </div>
            ))}
          </div>
        </div>
      </Cap>
      <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}}`}</style>
    </Section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   HITL — 5 approval gates
   ══════════════════════════════════════════════════════════════════════════ */
function HITL() {
  const { mob, tab } = useBreakpoint();
  const gates = [
    { icon:'📋', label:'Migration Plan', sub:'Before a single object is converted, your team reviews scope, effort estimate, risk flags, and recommended approach. Nothing moves forward without your sign-off.', risk:'Low — review only, nothing executed', consequence:'Defines the entire project scope and timeline.' },
    { icon:'💰', label:'Infrastructure Cost', sub:'Cloud infrastructure costs surfaced before anything is provisioned. Compute, storage, networking — fully itemised with reserved instance recommendations. No surprise bills.', risk:'Low — no resources created yet', consequence:'Prevents cloud cost overruns before they happen.' },
    { icon:'🔒', label:'Schema Deployment', sub:'Deploying schema changes to your target database requires explicit approval. Irreversible changes — column renames, constraint additions, table drops — are never made automatically.', risk:'Medium — schema changes are hard to reverse', consequence:'Prevents schema drift between source and target.' },
    { icon:'🚚', label:'Data Migration', sub:'Moving production data is the highest-risk step in any migration. RevMigrate stops and waits for your team to confirm. Every. Single. Time.', risk:'High — production data movement', consequence:'The single most important human checkpoint in the pipeline.' },
    { icon:'✅', label:'Final Validation', sub:'After automated checks complete, your team reviews row counts, integrity results, and performance benchmarks before the migration is marked complete. Final sign-off is always yours.', risk:'Low — all automated checks already passed', consequence:'Your confirmation that the migration is production-ready.' },
  ];

  const [active, setActive] = useState(null);

  return (
    <Section style={{ background:BG }}>
      <Cap>
        <Reveal>
          <Eyebrow>Human in the Loop</Eyebrow>
          <H2>Your team approves. The AI executes.</H2>
          <Body style={{ maxWidth:580 }}>
            RevMigrate is built on a simple principle: AI should do the heavy lifting, but humans should stay in control of the decisions that carry real business risk. Five approval gates. Every one of them yours.
          </Body>
        </Reveal>

        <Reveal delay={100}>
          {tab ? (
            /* ── Tablet/mobile: stacked gate buttons + detail below ── */
            <div style={{ marginTop:52 }}>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:24 }}>
                {gates.map((g,i) => (
                  <button key={i} onClick={()=>setActive(active===i?null:i)} style={{
                    display:'flex', alignItems:'center', gap:8, padding:'10px 16px',
                    cursor:'pointer', borderRadius:8,
                    background: active===i ? `rgba(${R},.08)` : 'rgba(255,255,255,.03)',
                    border: `1px solid ${active===i ? `rgba(${R},.3)` : 'rgba(255,255,255,.08)'}`,
                    fontFamily:"'DM Sans',system-ui,sans-serif",
                    fontSize:12.5, fontWeight:active===i?700:400,
                    color:active===i?C:'#9B90BA',
                    transition:'all .2s ease',
                  }}>
                    <span style={{ fontSize:16 }}>{g.icon}</span>
                    <span>{g.label}</span>
                  </button>
                ))}
              </div>
              {active !== null && (
                <div key={active} style={{ padding:'24px', background:'rgba(255,255,255,.025)', border:'1px solid rgba(255,255,255,.07)', borderRadius:10, animation:'fadeIn .3s ease both' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
                    <span style={{ fontSize:24 }}>{gates[active].icon}</span>
                    <div>
                      <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(16px,1.7vw,22px)', fontWeight:700, color:'#EDE9F8', margin:0 }}>{gates[active].label}</h3>
                      <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, color:`rgba(${R},.6)`, margin:'4px 0 0', fontWeight:600 }}>Approval Gate {active+1} of 5</p>
                    </div>
                  </div>
                  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:14, fontWeight:300, lineHeight:1.8, color:'#9B90BA', margin:'0 0 20px' }}>{gates[active].sub}</p>
                  <div style={{ display:'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap:12 }}>
                    <div style={{ padding:'14px 16px', background:`rgba(${R},.06)`, border:`1px solid rgba(${R},.18)`, borderRadius:8 }}>
                      <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:`rgba(${R},.6)`, margin:'0 0 6px' }}>Risk Level</p>
                      <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, color:'#C8C0EC', margin:0 }}>{gates[active].risk}</p>
                    </div>
                    <div style={{ padding:'14px 16px', background:'rgba(255,255,255,.03)', border:'1px solid rgba(255,255,255,.07)', borderRadius:8 }}>
                      <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:'rgba(200,192,236,0.45)', margin:'0 0 6px' }}>Why It Matters</p>
                      <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, color:'#C8C0EC', margin:0 }}>{gates[active].consequence}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* ── Desktop: sidebar + panel ── */
            <div style={{ display:'flex', alignItems:'stretch', marginTop:52, borderRadius:12, overflow:'hidden', border:'1px solid rgba(255,255,255,.07)' }}>
              <div style={{ width:260, flexShrink:0, borderRight:'1px solid rgba(255,255,255,.07)' }}>
                {gates.map((g,i) => (
                  <button key={i} onClick={()=>setActive(active===i?null:i)} style={{ width:'100%', display:'flex', alignItems:'center', gap:12, padding:'16px 20px', cursor:'pointer', background:active===i?`rgba(${R},.07)`:'transparent', border:'none', textAlign:'left', borderBottom:i<4?'1px solid rgba(255,255,255,.05)':'none', borderLeft:`2px solid ${active===i?C:'transparent'}`, transition:'all .2s ease' }}>
                    <span style={{ fontSize:18, flexShrink:0, opacity:active===i?1:.6 }}>{g.icon}</span>
                    <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:active===i?700:400, color:active===i?'#EDE9F8':'#9B90BA', transition:'color .2s' }}>{g.label}</span>
                    <span style={{ marginLeft:'auto', fontSize:11, color:`rgba(${R},.5)`, opacity:active===i?1:0 }}>→</span>
                  </button>
                ))}
              </div>
              <div style={{ flex:1, padding:active!==null?'32px 36px':'0', display:'flex', alignItems:'center', justifyContent:active===null?'center':'flex-start', background:'rgba(255,255,255,.015)', transition:'all .3s ease' }}>
                {active === null ? (
                  <p style={{ fontFamily:"'Fraunces',Georgia,serif", fontStyle:'italic', fontWeight:300, fontSize:15, color:'rgba(200,192,236,0.38)', textAlign:'center', margin:0 }}>Select an approval gate to see how it works</p>
                ) : (
                  <div key={active} style={{ animation:'fadeIn .3s ease both' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
                      <span style={{ fontSize:28 }}>{gates[active].icon}</span>
                      <div>
                        <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(18px,1.7vw,24px)', fontWeight:700, color:'#EDE9F8', margin:0 }}>{gates[active].label}</h3>
                        <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, color:`rgba(${R},.6)`, margin:'4px 0 0', fontWeight:600 }}>Approval Gate {active+1} of 5</p>
                      </div>
                    </div>
                    <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:15, fontWeight:300, lineHeight:1.8, color:'#9B90BA', margin:'0 0 24px' }}>{gates[active].sub}</p>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                      <div style={{ padding:'14px 16px', background:`rgba(${R},.06)`, border:`1px solid rgba(${R},.18)`, borderRadius:8 }}>
                        <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:`rgba(${R},.6)`, margin:'0 0 6px' }}>Risk Level</p>
                        <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, color:'#C8C0EC', margin:0 }}>{gates[active].risk}</p>
                      </div>
                      <div style={{ padding:'14px 16px', background:'rgba(255,255,255,.03)', border:'1px solid rgba(255,255,255,.07)', borderRadius:8 }}>
                        <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:'rgba(200,192,236,0.45)', margin:'0 0 6px' }}>Why It Matters</p>
                        <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, color:'#C8C0EC', margin:0 }}>{gates[active].consequence}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </Reveal>

        <Reveal delay={200}>
          <div style={{ marginTop:32, padding:'24px 28px', background:`rgba(${R},.05)`, border:`1px solid rgba(${R},.15)`, borderRadius:12, display:'flex', gap:20, alignItems:'flex-start' }}>
            <div style={{ fontSize:24, flexShrink:0 }}>💬</div>
            <div>
              <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:`rgba(${R},.7)`, margin:'0 0 8px' }}>Natural Language Override</p>
              <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:14.5, fontWeight:300, lineHeight:1.75, color:'#C8C0EC', margin:0 }}>
                At any approval gate, your team can give specific instructions in plain English. <em style={{ color:`rgba(${R},.75)`, fontStyle:'italic' }}>"Rewrite SP_GetPatient using CTEs instead of temp tables."</em> The AI reads that feedback and re-executes accordingly. No developer required.
              </p>
            </div>
          </div>
        </Reveal>
      </Cap>
    </Section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   ECOSYSTEM
   ══════════════════════════════════════════════════════════════════════════ */
function Ecosystem() {
  const { mob } = useBreakpoint();
  const agents = [
    { name:'RevSec', role:'AI CISO', color:'#10B981', rgb:'16,185,129', position:'Before migration begins', tagline:'Know your risk before you move.', body:'The AI CISO assesses your existing database environment for security vulnerabilities, compliance gaps, and data sensitivity. It produces a risk profile that informs the migration plan — so your team knows exactly what needs protecting, what needs remediating, and what must never leave the firewall.' },
    { name:'RevModernize', role:'AI Architect', color:'#00D4AA', rgb:'0,212,170', position:'Defines the target state', tagline:'Design the right architecture, then build it.', body:'The AI Architect analyses your existing database architecture and recommends the optimal target design — which stored procedures to keep in the database, which to move to the application layer, and which need manual re-engineering. This blueprint is handed directly to RevMigrate as conversion instructions.' },
    { name:'RevMigrate', role:'AI DBA', color:C, rgb:R, position:'Executes the full pipeline', tagline:'Plan. Convert. Deploy. Validate.', lead:true, body:'Once the CISO has cleared the risk profile and the Architect has defined the target state, RevMigrate orchestrates the full migration pipeline — automatically, with human approval at every critical gate. This is where the plan becomes reality.' },
  ];

  return (
    <Section style={{ background:`rgba(${R},.02)`, borderTop:`1px solid rgba(${R},.07)`, borderBottom:`1px solid rgba(${R},.07)` }}>
      <Cap>
        <Reveal>
          <Eyebrow>The RevModernize Ecosystem</Eyebrow>
          <H2>Three AI specialists. One coordinated outcome.</H2>
          <Body style={{ maxWidth:600 }}>RevMigrate doesn't operate in isolation. It's part of the RevModernize platform — a team of AI agents that work together to guide your organisation through the complete enterprise modernisation journey.</Body>
        </Reveal>

        {/* Flow diagram — hidden on mobile, shown as stacked text */}
        {!mob && (
          <Reveal delay={80}>
            <div style={{ display:'flex', alignItems:'center', gap:0, marginTop:52, marginBottom:52, padding:'20px 24px', background:'rgba(255,255,255,.02)', border:'1px solid rgba(255,255,255,.06)', borderRadius:12 }}>
              {agents.map((a,i) => (
                <React.Fragment key={i}>
                  <div style={{ flex:1, textAlign:'center', padding:'0 12px' }}>
                    <div style={{ width:10, height:10, borderRadius:'50%', background:a.color, margin:'0 auto 6px', boxShadow:a.lead?`0 0 16px ${a.color}`:'none' }}/>
                    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10, fontWeight:700, color:`rgba(${a.rgb},.75)`, letterSpacing:1, textTransform:'uppercase', marginBottom:3 }}>{a.role}</div>
                    <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:14, fontWeight:700, color:'#D4CEF0' }}>{a.name}</div>
                    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10, color:'rgba(200,192,236,0.45)', marginTop:3 }}>{a.position}</div>
                  </div>
                  {i < 2 && (
                    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2, flexShrink:0, padding:'0 4px' }}>
                      <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10, color:'rgba(200,192,236,0.3)', whiteSpace:'nowrap' }}>hands off to</div>
                      <div style={{ fontSize:18, color:'rgba(200,192,236,0.38)' }}>→</div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </Reveal>
        )}

        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:2, marginTop: mob ? 52 : 0 }}>
          {agents.map((a,i) => (
            <Reveal key={i} delay={i*90}>
              <div style={{ padding:'32px 28px', height:'100%', background:a.lead?`rgba(${a.rgb},.06)`:'rgba(255,255,255,.02)', border:`1px solid ${a.lead?`rgba(${a.rgb},.25)`:'rgba(255,255,255,.06)'}`, position:'relative', transition:'all .2s ease', cursor:'default' }}
                onMouseEnter={e=>{e.currentTarget.style.background=`rgba(${a.rgb},.08)`;e.currentTarget.style.borderColor=`rgba(${a.rgb},.3)`;}}
                onMouseLeave={e=>{e.currentTarget.style.background=a.lead?`rgba(${a.rgb},.06)`:'rgba(255,255,255,.02)';e.currentTarget.style.borderColor=a.lead?`rgba(${a.rgb},.25)`:'rgba(255,255,255,.06)';}}
              >
                {a.lead && <div style={{ position:'absolute', top:18, right:18, padding:'2px 9px', borderRadius:100, background:`rgba(${a.rgb},.15)`, border:`1px solid rgba(${a.rgb},.3)`, fontSize:9, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:a.color }}>This is RevMigrate</div>}
                <div style={{ width:8, height:8, borderRadius:'50%', background:a.color, marginBottom:16, boxShadow:`0 0 12px ${a.color}` }}/>
                <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:`rgba(${a.rgb},.65)`, marginBottom:5 }}>{a.role}</div>
                <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(18px,1.6vw,22px)', fontWeight:800, color:'#EDE9F8', margin:'0 0 6px' }}>{a.name}</h3>
                <p style={{ fontFamily:"'Fraunces',Georgia,serif", fontStyle:'italic', fontSize:13, fontWeight:300, color:`rgba(${a.rgb},.7)`, margin:'0 0 16px' }}>{a.tagline}</p>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:300, lineHeight:1.75, color:'#9B90BA', margin:0 }}>{a.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Cap>
    </Section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   CAPABILITIES
   ══════════════════════════════════════════════════════════════════════════ */
function Capabilities() {
  const caps = [
    { icon:'🏢', headline:'Multi-Tenant & Secure', body:'Every migration is fully isolated at the organisation level. Multiple teams can run concurrent migrations with zero risk of data crossover. Built for enterprise from day one.' },
    { icon:'⚡', headline:'Large Database Ready', body:'Tested on real enterprise databases with thousands of tables and hundreds of stored procedures. One failed conversion never blocks the rest of your migration.' },
    { icon:'💬', headline:'Natural Language Control', body:'Interact with RevMigrate in plain English. Start a migration, check progress, approve decisions, or give specific conversion instructions — all through conversation.' },
    { icon:'📊', headline:'Full Audit Trail', body:'Every agent action, human decision, approval, and retry is logged with a timestamp and actor. Compliance, post-migration review, and debugging are all covered.' },
    { icon:'🔄', headline:'Resumable Across Days', body:"Enterprise migrations take days or weeks. RevMigrate remembers exactly where it left off — close the browser and return days later to pick up right where the migration paused." },
    { icon:'📄', headline:'Migration Reports', body:'A comprehensive PDF report is generated at the end of every migration — covering what was converted, what was skipped, validation results, and a full summary ready for stakeholders.' },
  ];
  return (
    <Section style={{ background:BG }}>
      <Cap>
        <Reveal>
          <Eyebrow>Key Capabilities</Eyebrow>
          <H2>Built for enterprise scale. Designed for enterprise trust.</H2>
        </Reveal>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))', gap:2, marginTop:48 }}>
          {caps.map((cap,i) => (
            <Reveal key={i} delay={i*60}>
              <div style={{ padding:'28px 26px', height:'100%', background:'rgba(255,255,255,.025)', border:'1px solid rgba(255,255,255,.07)', transition:'all .2s ease', cursor:'default' }}
                onMouseEnter={e=>{e.currentTarget.style.background=`rgba(${R},.05)`;e.currentTarget.style.borderColor=`rgba(${R},.2)`;}}
                onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,.025)';e.currentTarget.style.borderColor='rgba(255,255,255,.07)';}}
              >
                <div style={{ fontSize:24, marginBottom:14 }}>{cap.icon}</div>
                <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(16px,1.4vw,19px)', fontWeight:700, color:'#DDD8F2', margin:'0 0 10px' }}>{cap.headline}</h3>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, fontWeight:300, lineHeight:1.75, color:'#9B90BA', margin:0 }}>{cap.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Cap>
    </Section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   METRICS
   ══════════════════════════════════════════════════════════════════════════ */
function Metrics() {
  const stats = [
    { val:90, suffix:'%', label:'reduction in manual conversion effort', sub:"What your DBA team does by hand in months, RevMigrate handles overnight. They review the output — they don't produce it." },
    { val:4,  suffix:'×', label:'faster time-to-migrate vs traditional projects', sub:'18-month migrations compressed into weeks. Your engineering team freed to build product, not convert SQL.' },
    { val:100, suffix:'%', label:'audit trail on every decision made', sub:'Every approval, every rejection, every AI recommendation — logged with timestamp and actor. Compliance is built in.' },
    { val:0, suffix:'', label:'surprises at cutover', sub:'Five approval gates, automated validation, and row-count verification mean nothing unexpected reaches production.' },
  ];
  return (
    <Section style={{ background:`rgba(${R},.025)`, borderTop:`1px solid rgba(${R},.07)`, borderBottom:`1px solid rgba(${R},.07)` }}>
      <Cap>
        <Reveal>
          <Eyebrow>Outcomes</Eyebrow>
          <H2>Move faster. Migrate smarter. Zero guesswork.</H2>
        </Reveal>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:2, marginTop:48 }}>
          {stats.map((s,i) => (
            <Reveal key={i} delay={i*80}>
              <div style={{ padding:'36px 28px', height:'100%', background:`rgba(${R},.06)`, border:`1px solid rgba(${R},.15)` }}>
                <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:'clamp(44px,4.5vw,68px)', lineHeight:1, color:C, marginBottom:10 }}>
                  <Counter to={s.val} suffix={s.suffix}/>
                </div>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12, fontWeight:700, color:'#C8C0EC', margin:'0 0 12px', textTransform:'uppercase', letterSpacing:.5 }}>{s.label}</p>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:300, lineHeight:1.75, color:'#9B90BA', margin:0 }}>{s.sub}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Cap>
    </Section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   WHO IT'S FOR
   ══════════════════════════════════════════════════════════════════════════ */
function WhoItsFor() {
  const { mob, tab } = useBreakpoint();
  const [active, setActive] = useState(0);
  const personas = [
    {
      role:'CTO / VP Engineering',
      icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
      headline:'Compress an 18-month migration into weeks. Free your team to build AI.',
      problem:"Legacy database is blocking your entire AI-readiness roadmap. Your engineering team is 40% consumed by manual migration work instead of shipping product. The board is asking why you're not moving faster.",
      delivers:'A predictable, auditable migration path with a clear timeline. Engineering freed from SQL conversion to focus on AI integrations and product work that actually moves the needle.',
      value:'Board-ready migration plan · Predictable timeline · Engineering capacity back',
    },
    {
      role:'DBA / Data Engineering',
      icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>,
      headline:"Stop hand-converting stored procedures. Focus on the complex edge cases that actually need you.",
      problem:"You're spending 80% of your time on routine object conversion that an AI can handle — and 20% on the complex stored procedures and architecture decisions that actually need your expertise.",
      delivers:'RevMigrate handles routine conversion automatically. You review the AI output, guide the complex edge cases, and give natural language instructions when the AI needs correction. Your expertise applied where it matters.',
      value:'Routine conversion automated · Complex work stays with you · Natural language override',
    },
    {
      role:'CISO / Compliance Lead',
      icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>,
      headline:'Every decision logged and auditable. Compliance built in, not bolted on.',
      problem:"You get called in when the migration is 80% done and asked to sign off on infrastructure you've never reviewed. Compliance is always an afterthought, and audit evidence is assembled manually after the fact.",
      delivers:'Five approval gates with full AI context at each step. Every decision logged with who approved it, when, and why. A complete audit trail generated automatically — ready for compliance review without manual evidence gathering.',
      value:'Approval gates at every risk point · Complete audit trail · Compliance by default',
    },
  ];

  const p = personas[active];

  return (
    <Section style={{ background:BG }}>
      <Cap>
        <Reveal>
          <Eyebrow>Who It's For</Eyebrow>
          <H2>Built for teams who can't afford to get migration wrong.</H2>
        </Reveal>

        {tab ? (
          /* ── Tablet/mobile: pill buttons + stacked content ── */
          <div style={{ marginTop:40 }}>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:28 }}>
              {personas.map((per,i) => (
                <button key={i} onClick={()=>setActive(i)} style={{
                  display:'flex', alignItems:'center', gap:8, padding:'10px 16px',
                  cursor:'pointer', borderRadius:8,
                  background: i===active?`rgba(${R},.08)`:'rgba(255,255,255,.03)',
                  border:`1px solid ${i===active?`rgba(${R},.3)`:'rgba(255,255,255,.08)'}`,
                  fontFamily:"'DM Sans',system-ui,sans-serif",
                  fontSize:12.5, fontWeight:i===active?700:400,
                  color:i===active?C:'#9B90BA',
                  transition:'all .2s ease',
                }}>
                  <span style={{ color:i===active?C:'rgba(200,192,236,0.55)' }}>{per.icon}</span>
                  <span>{per.role}</span>
                </button>
              ))}
            </div>
            <div key={active} style={{ padding:'24px', background:'rgba(255,255,255,.025)', border:'1px solid rgba(255,255,255,.07)', borderRadius:10, animation:'fadeIn .35s ease both' }}>
              <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(17px,1.7vw,22px)', fontWeight:700, color:'#EDE9F8', margin:'0 0 16px', lineHeight:1.3 }}>{p.headline}</h3>
              <div style={{ display:'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap:20, marginBottom:20 }}>
                <div>
                  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:'rgba(200,192,236,0.4)', margin:'0 0 8px' }}>Their Problem</p>
                  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, fontWeight:300, lineHeight:1.75, color:'#9B90BA', margin:0 }}>{p.problem}</p>
                </div>
                <div>
                  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:`rgba(${R},.5)`, margin:'0 0 8px' }}>What RevMigrate Delivers</p>
                  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, fontWeight:300, lineHeight:1.75, color:'#9B90BA', margin:0 }}>{p.delivers}</p>
                </div>
              </div>
              <div style={{ padding:'12px 18px', background:`rgba(${R},.08)`, border:`1px solid rgba(${R},.18)`, borderRadius:8 }}>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:`rgba(${R},.7)`, margin:'0 0 4px' }}>Key Value</p>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, color:`rgba(${R},.85)`, margin:0 }}>{p.value}</p>
              </div>
            </div>
          </div>
        ) : (
          /* ── Desktop: sidebar + full panel ── */
          <div style={{ display:'grid', gridTemplateColumns:'220px 1fr', gap:2, marginTop:52 }}>
            <div style={{ display:'flex', flexDirection:'column' }}>
              {personas.map((per,i) => (
                <button key={i} onClick={()=>setActive(i)} style={{ display:'flex', alignItems:'center', gap:12, padding:'15px 18px', cursor:'pointer', background:i===active?`rgba(${R},.07)`:'transparent', border:'none', textAlign:'left', borderLeft:`2px solid ${i===active?C:'rgba(255,255,255,.07)'}`, transition:'all .2s ease' }}>
                  <span style={{ color:i===active?C:'rgba(200,192,236,0.55)', flexShrink:0 }}>{per.icon}</span>
                  <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12.5, fontWeight:i===active?700:400, color:i===active?'#EDE9F8':'rgba(200,192,236,0.55)', transition:'color .2s' }}>{per.role}</span>
                </button>
              ))}
            </div>
            <div key={active} style={{ padding:'30px 36px', background:'rgba(255,255,255,.025)', border:'1px solid rgba(255,255,255,.07)', animation:'fadeIn .35s ease both' }}>
              <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(18px,1.7vw,24px)', fontWeight:700, color:'#EDE9F8', margin:'0 0 20px', lineHeight:1.3 }}>{p.headline}</h3>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24, marginBottom:24 }}>
                <div>
                  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:'rgba(200,192,236,0.4)', margin:'0 0 8px' }}>Their Problem</p>
                  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, fontWeight:300, lineHeight:1.75, color:'#9B90BA', margin:0 }}>{p.problem}</p>
                </div>
                <div>
                  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:`rgba(${R},.5)`, margin:'0 0 8px' }}>What RevMigrate Delivers</p>
                  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, fontWeight:300, lineHeight:1.75, color:'#9B90BA', margin:0 }}>{p.delivers}</p>
                </div>
              </div>
              <div style={{ padding:'12px 18px', background:`rgba(${R},.08)`, border:`1px solid rgba(${R},.18)`, borderRadius:8 }}>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:`rgba(${R},.7)`, margin:'0 0 4px' }}>Key Value</p>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, color:`rgba(${R},.85)`, margin:0 }}>{p.value}</p>
              </div>
            </div>
          </div>
        )}
      </Cap>
    </Section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   CTA
   ══════════════════════════════════════════════════════════════════════════ */
function CTA() {
  const { mob } = useBreakpoint();
  return (
    <section style={{ background:`linear-gradient(135deg,rgba(${R},.1) 0%,rgba(45,24,84,.7) 55%,${BG} 100%)`, borderTop:`1px solid rgba(${R},.12)`, padding:`${SECTION_PAD} 0`, textAlign:'center', position:'relative', overflow:'hidden' }}>
      <div aria-hidden="true" style={{ position:'absolute', top:'-20%', left:'50%', transform:'translateX(-50%)', width:700, height:700, borderRadius:'50%', background:`radial-gradient(circle,rgba(${R},.12) 0%,transparent 65%)`, filter:'blur(90px)', pointerEvents:'none' }}/>
      <div style={{ position:'relative', zIndex:1 }}>
        <Reveal>
          <Eyebrow>Ready to migrate?</Eyebrow>
          <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:'clamp(30px,4vw,58px)', lineHeight:1.08, letterSpacing:'-1.5px', color:'#EDE9F8', margin:'0 auto 20px', maxWidth:720 }}>
            Move faster.<br/>Migrate smarter.<br/><span style={{ color:C }}>Zero guesswork.</span>
          </h2>
          <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:'clamp(15px,1.15vw,18px)', fontWeight:300, lineHeight:1.75, color:'#9B90BA', margin:'0 auto 48px', maxWidth:500 }}>
            See RevMigrate in action on a real enterprise database — from AI planning to human approval to validated go-live.
          </p>
          <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap', padding: mob ? '0 16px' : '0' }}>
            <button style={{ padding:'16px 36px', borderRadius:10, cursor:'pointer', background:C, color:'#09060F', border:'none', fontSize:16, fontWeight:800, fontFamily:"'DM Sans',system-ui,sans-serif", boxShadow:`0 8px 32px rgba(${R},.4)`, transition:'all .22s ease', flex: mob ? '1 1 auto' : 'none' }}
              onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow=`0 14px 44px rgba(${R},.58)`;}}
              onMouseLeave={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow=`0 8px 32px rgba(${R},.4)`;}}
            >Book a Demo</button>
            <button style={{ padding:'16px 28px', borderRadius:10, cursor:'pointer', background:'rgba(255,255,255,.05)', color:'#C8C0EC', border:'1px solid rgba(255,255,255,.13)', fontSize:16, fontWeight:400, fontFamily:"'DM Sans',system-ui,sans-serif", transition:'all .22s ease', flex: mob ? '1 1 auto' : 'none' }}
              onMouseEnter={e=>{e.currentTarget.style.background='rgba(255,255,255,.1)';e.currentTarget.style.color='#EDE9F8';}}
              onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,.05)';e.currentTarget.style.color='#C8C0EC';}}
            >Request a Migration Assessment</button>
            <button style={{ padding:'16px 24px', borderRadius:10, cursor:'pointer', background:'transparent', color:`rgba(${R},.75)`, border:`1px solid rgba(${R},.25)`, fontSize:14, fontWeight:400, fontFamily:"'DM Sans',system-ui,sans-serif", transition:'all .22s ease', width: mob ? '100%' : 'auto' }}
              onMouseEnter={e=>{e.currentTarget.style.background=`rgba(${R},.08)`;e.currentTarget.style.color=C;}}
              onMouseLeave={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.color=`rgba(${R},.75)`;}}
            >Talk to an Expert</button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   ROOT
   ══════════════════════════════════════════════════════════════════════════ */
export default function AIDBA() {
  return (
    <>
      <Helmet>
        <title>AI DBA — RevMigrate | Sybase to PostgreSQL, Oracle to AWS & SQL Server Migration | RevUpAI</title>
        <meta name="description" content="RevMigrate is your AI DBA — migrating Sybase, Oracle, and SQL Server to PostgreSQL 16 or AWS RDS in 4–12 weeks. 50%+ cost reduction, 88% less effort. AWS migration support included. Human sign-off at every critical stage." />
        <meta name="keywords" content="Sybase to PostgreSQL migration, Oracle to PostgreSQL migration, SQL Server migration PostgreSQL, AWS RDS migration, AWS database migration, Oracle to AWS migration, database modernization AI, AI DBA, legacy database migration, Sybase migration, database cost reduction, PostgreSQL migration, cloud database migration, Sybase ASE migration" />
        <link rel="canonical" href="https://revupai.com/ai-dba" />
        <meta property="og:title" content="AI DBA — Sybase, Oracle & SQL Server to PostgreSQL / AWS | RevUpAI" />
        <meta property="og:description" content="Migrate Sybase, Oracle, SQL Server to PostgreSQL or AWS RDS in weeks. 50%+ cost reduction. 88% less effort." />
        <meta property="og:url" content="https://revupai.com/ai-dba" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://revupai.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI DBA — Sybase & Oracle to PostgreSQL / AWS | RevUpAI" />
        <meta name="twitter:description" content="Migrate Sybase, Oracle, SQL Server to PostgreSQL or AWS in weeks. 50%+ cost reduction." />
        <meta name="twitter:image" content="https://revupai.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'RevMigrate — AI DBA',
          serviceType: 'Database Migration & Modernization',
          provider: { '@type': 'Organization', name: 'RevUpAI', url: 'https://revupai.com' },
          areaServed: 'Worldwide',
          url: 'https://revupai.com/ai-dba',
          description: 'AI-operated database migration — Sybase, Oracle and SQL Server to PostgreSQL 16 or AWS RDS in 4–12 weeks, 50%+ cost reduction, with human sign-off at every critical stage.',
        })}</script>
      </Helmet>
      <style>{`
        @keyframes fadeUp  { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:none} }
        @keyframes fadeIn  { from{opacity:0} to{opacity:1} }
        @keyframes pulse   { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.4;transform:scale(.8)} }
        * { box-sizing:border-box; }
      `}</style>
      <div style={{ background:BG, minHeight:'100vh' }}>
        <Hero/>
        <Provocation/>
        <Problem/>
        <Pipeline/>
        <HITL/>
        <Ecosystem/>
        <Capabilities/>
        <Metrics/>
        <WhoItsFor/>
        <CTA/>
      </div>
    </>
  );
}